import React, { useState } from 'react';
import { useUser } from '../contexts/TestAuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { Link } from 'react-router-dom';
import { Palette, Key, Building2, User, Shield, Users, Activity, Briefcase, AlertTriangle, HeartPulse, FileText, Calculator } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import BrandingSettings from '../components/BrandingSettings';
import APISettings from '../components/APISettings';
import TeamManagement from '../components/TeamManagement';
import LawFirmManagement from '../components/LawFirmManagement';
import ErrorLogViewer from '../components/ErrorLogViewer';
import DataHealthWidget from '../components/dashboard/DataHealthWidget';
import EventTemplatesManager from '../components/EventTemplatesManager';
import FutureClosureConfigPanel from '../components/FutureClosureConfigPanel';

type TabType = 'organization' | 'lawfirms' | 'team' | 'branding' | 'api' | 'errors' | 'datahealth' | 'templates' | 'closure_config';

export default function SettingsPage() {
  const { user } = useUser();
  const { role } = usePermissions();
  const [activeTab, setActiveTab] = useState<TabType>('organization');

  const orgId = user?.id || 'default-org';

  const tabs = [
    { id: 'organization', label: 'Organization', icon: Building2 },
    { id: 'lawfirms', label: 'Law Firms', icon: Briefcase },
    { id: 'team', label: 'Team Members', icon: Users },
    { id: 'templates', label: 'Event Templates', icon: FileText },
    { id: 'branding', label: 'Branding', icon: Palette },
    { id: 'api', label: 'API & Integrations', icon: Key },
    { id: 'closure_config', label: 'Closure Config', icon: Calculator }
  ] as const;

  return (
    <AppLayout>
      <div className="p-6">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-xl font-bold text-gray-900">Settings</h1>
          <p className="text-sm text-gray-500">Manage your organization and preferences</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Tabs Navigation */}
          <div className="lg:w-56 flex-shrink-0">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-2 sticky top-24">
              <nav className="space-y-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition text-left text-sm ${
                        activeTab === tab.id
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className={`w-4 h-4 ${
                        activeTab === tab.id ? 'text-blue-600' : 'text-gray-400'
                      }`} />
                      {tab.label}
                    </button>
                  );
                })}
                
                {/* Activity Log Link - Owners Only */}
                {(role === 'owner' || role === 'admin') && (
                  <>
                    <div className="border-t border-gray-200 my-2" />
                    <button
                      onClick={() => setActiveTab('datahealth')}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition text-left text-sm ${
                        activeTab === 'datahealth'
                          ? 'bg-indigo-50 text-indigo-700 font-medium'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <HeartPulse className={`w-4 h-4 ${activeTab === 'datahealth' ? 'text-indigo-600' : 'text-gray-400'}`} />
                      Data Health
                      <span className="ml-auto bg-indigo-100 text-indigo-700 text-xs px-1.5 py-0.5 rounded font-medium">Admin</span>
                    </button>
                    <button
                      onClick={() => setActiveTab('errors')}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition text-left text-sm ${
                        activeTab === 'errors'
                          ? 'bg-red-50 text-red-700 font-medium'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <AlertTriangle className={`w-4 h-4 ${activeTab === 'errors' ? 'text-red-600' : 'text-gray-400'}`} />
                      Error Logs
                      <span className="ml-auto bg-red-100 text-red-700 text-xs px-1.5 py-0.5 rounded font-medium">Admin</span>
                    </button>
                    <Link
                      to="/settings/activity"
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition text-left text-sm text-gray-600 hover:bg-gray-50"
                    >
                      <Activity className="w-4 h-4" />
                      Activity Log
                      <span className="ml-auto bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded font-medium">Admin</span>
                    </Link>
                  </>
                )}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Organization Tab */}
            {activeTab === 'organization' && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-6 h-6 text-white" />
                    <div>
                      <h2 className="text-lg font-bold text-white">Organization Details</h2>
                      <p className="text-sm text-blue-100">Manage your organization information</p>
                    </div>
                  </div>
                </div>
                
                <div className="p-6 space-y-6">
                  {/* Organization Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Organization ID</label>
                      <div className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                        <code className="text-sm text-gray-600">{orgId.substring(0, 20)}...</code>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Used for API integrations</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
                      <div className="px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-lg">
                        <span className="text-sm font-semibold text-blue-700">Professional</span>
                      </div>
                    </div>
                  </div>

                  {/* Team Members - Now using TeamManagement */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium text-gray-900">Team Members</h3>
                      <button
                        onClick={() => setActiveTab('team')}
                        className="px-3 py-1 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition"
                      >
                        Manage Team →
                      </button>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <p className="text-sm text-blue-800">
                        🎉 Team management is now available! Invite members, assign roles, and control access.
                      </p>
                    </div>
                  </div>

                  {/* Security */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Shield className="w-4 h-4 text-gray-400" />
                      <h3 className="font-medium text-gray-900">Security</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-sm font-medium text-green-800">Clerk Authentication</span>
                        </div>
                        <p className="text-xs text-green-600">Secure authentication enabled</p>
                      </div>
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span className="text-sm font-medium text-blue-800">API Security</span>
                        </div>
                        <p className="text-xs text-blue-600">Rate limiting & key authentication</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Law Firms Tab */}
            {activeTab === 'lawfirms' && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <LawFirmManagement />
              </div>
            )}

            {/* Branding Tab */}
            {activeTab === 'branding' && (
              <BrandingSettings 
                orgId={orgId} 
                onSave={(config) => console.log('Branding saved:', config)}
              />
            )}

            {/* Team Tab */}
            {activeTab === 'team' && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="p-6">
                  <TeamManagement />
                </div>
              </div>
            )}

            {/* Event Templates Tab */}
            {activeTab === 'templates' && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-4">
                  <div className="flex items-center gap-3">
                    <FileText className="w-6 h-6 text-white" />
                    <div>
                      <h2 className="text-lg font-bold text-white">Event Templates</h2>
                      <p className="text-sm text-indigo-100">Pre-configured settings for different disaster types</p>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <EventTemplatesManager />
                </div>
              </div>
            )}

            {/* API Tab */}
            {activeTab === 'api' && (
              <APISettings orgId={orgId} />
            )}

            {/* Future Closure Config Tab */}
            {activeTab === 'closure_config' && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-emerald-600 to-teal-700 px-6 py-4">
                  <div className="flex items-center gap-3">
                    <Calculator className="w-6 h-6 text-white" />
                    <div>
                      <h2 className="text-lg font-bold text-white">Future Closure Configuration</h2>
                      <p className="text-sm text-emerald-100">Manage default multipliers, continuation rates, and calculation parameters</p>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <FutureClosureConfigPanel />
                </div>
              </div>
            )}

            {/* Error Logs Tab - Owners/Admins Only */}
            {activeTab === 'errors' && (role === 'owner' || role === 'admin') && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-6 h-6 text-white" />
                    <div>
                      <h2 className="text-lg font-bold text-white">API Error Logs</h2>
                      <p className="text-sm text-red-100">Monitor and resolve frontend API errors</p>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <ErrorLogViewer />
                </div>
              </div>
            )}

            {/* Data Health Tab - Owners/Admins Only */}
            {activeTab === 'datahealth' && (role === 'owner' || role === 'admin') && (
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl px-6 py-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <HeartPulse className="w-6 h-6 text-white" />
                    <div>
                      <h2 className="text-lg font-bold text-white">Data Health Dashboard</h2>
                      <p className="text-sm text-blue-100">Monitor data quality metrics across all claims</p>
                    </div>
                  </div>
                </div>
                <DataHealthWidget />
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
